import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Editor } from './Editor';
import {
    CSSEditor,
    CSSEditorAction,
    CSSEditorContent,
    CSSEditorTab,
    CSSEditorHeader,
    CSSEditorLabel,
    CSSEditorMask,
    CSSEditorMenu,
    CSSEditorMenuButton,
} from './style';
import { Widget, WidgetEditor } from 'jamespot-front-business';
import { useIntl } from 'react-intl';
import { EDITOR_CONTENT_WIDTH } from './const';
import { EditorPosition } from './EditorPosition';
import { EditorTabWrapper } from './EditorTabWrapper';
import { EditorIsBusy } from './states/EditorIsBusy';
import { EditorIsLocked } from './states/EditorIsLocked';

import { EditorSave } from './components/EditorSave';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { useTheme } from 'styled-components';
import { EditorTabWidgetSelection } from './EditorTabWidgetSelection';
import { EditorTabDisplayLevel } from './EditorTabDisplayLevel';

const OVERRIDE_SAVE = [
    'widget-presence',
    'widget-quick-survey',
    'widget-check-list',
    'widget-emails-imap',
    'widget-contact-cardDav',
    'widget-calendar-calDav',
];

const Icon = commonComponentsRegistry.getLazyComponent('Icon');
const Tooltip = commonComponentsRegistry.getLazyComponent('Tooltip');

type EditorTabs = 'widget-settings' | 'widget-title' | 'widget-selection' | 'widget-level';
type EditorTab = {
    name: EditorTabs;
    label: string;
    icon: string;
    enable: boolean;
};

/*
    Editor wrapper (right part)
*/
export const EditorWrapper = ({ uniqid, position }: { uniqid: string; position: 'left' | 'right' }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const ref = useRef<HTMLDivElement>(null);
    const editor = useAppSelector((state) => WidgetEditor.selectors.selectEditor(state, uniqid), shallowEqual);

    const def = Widget.definition(editor?.name);

    const enableWidgetLevel = useMemo<boolean>(() => {
        return def.displayLevel !== undefined && Array.isArray(def.displayLevel) && def.displayLevel.length !== 0;
    }, [def]);
    const tabs: EditorTab[] = useMemo(() => {
        return [
            {
                name: 'widget-title',
                label: intl.formatMessage({ id: 'PANEL_Title' }),
                enable: def.panel.useWrapper,
                icon: 'icon-font',
            },
            {
                name: 'widget-selection',
                label: intl.formatMessage({ id: 'PANEL_Widget_Selection' }),
                enable: def.panel.useSelection,
                icon: 'icon-sliders',
            },
            {
                name: 'widget-settings',
                label: intl.formatMessage({ id: 'PANEL_Widget_Settings' }),
                enable: def.panel.useEditor,
                icon: 'icon-pen',
            },
            {
                name: 'widget-level',
                label: intl.formatMessage({ id: 'PANEL_Widget_Level' }),
                enable: enableWidgetLevel,
                icon: 'icon-edit',
            },
        ];
    }, [def, intl, enableWidgetLevel]);

    const defaultTab = useMemo<EditorTab | undefined>(() => {
        if (def.panel.useEditor) {
            return tabs.find((el) => el.name === 'widget-settings');
        }

        return tabs.filter((el) => el.enable)[0];
    }, [def, tabs]);

    const [currentTab, setCurrentTab] = useState<EditorTab | undefined>(defaultTab);

    const onClose = () => {
        dispatch(WidgetEditor.slice.actions.flushEditor({ uniqid }));
    };

    useEffect(() => {
        setTimeout(() => {
            if (ref && ref.current) {
                ref.current.className = ref.current.className + ' animate';
            }
        }, 0);
    }, []);

    return editor ? (
        <CSSEditorMask>
            <CSSEditor ref={ref} position={position}>
                <CSSEditorMenu>
                    {tabs
                        .filter((tab) => tab.enable)
                        .map((tab) => {
                            return (
                                <CSSEditorMenuButton
                                    key={tab.name}
                                    isActive={currentTab && currentTab.name === tab.name}
                                    onClick={() => setCurrentTab(tab)}>
                                    <Tooltip position={'left'} description={tab.label}>
                                        <Icon
                                            name={tab.icon}
                                            color={
                                                currentTab && currentTab.name === tab.name
                                                    ? theme.color.white
                                                    : theme.color.lavender
                                            }
                                        />
                                    </Tooltip>
                                </CSSEditorMenuButton>
                            );
                        })}
                </CSSEditorMenu>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: `${EDITOR_CONTENT_WIDTH}px` }}>
                    <CSSEditorHeader>
                        <EditorPosition uniqid={uniqid} position={editor.position} />
                        <CSSEditorLabel>
                            <h3>{currentTab ? currentTab.label : intl.formatMessage({ id: def.label })}</h3>
                        </CSSEditorLabel>
                        <CSSEditorAction>
                            <CSSEditorMenuButton onClick={onClose}>
                                <Icon name="icon-times" color={theme.color.lavender} size={26.75} />
                            </CSSEditorMenuButton>
                        </CSSEditorAction>
                    </CSSEditorHeader>
                    <CSSEditorContent>
                        <CSSEditorTab>
                            {currentTab && currentTab.name === 'widget-settings' && def.panel.useEditor && (
                                <Editor uniqid={editor.uniqid} />
                            )}
                            {currentTab && currentTab.name === 'widget-title' && def.panel.useWrapper && (
                                <EditorTabWrapper uniqid={editor.uniqid} />
                            )}
                            {currentTab && currentTab.name === 'widget-selection' && (
                                <EditorTabWidgetSelection uniqid={uniqid} />
                            )}
                            {currentTab && currentTab.name === 'widget-level' && enableWidgetLevel && (
                                <EditorTabDisplayLevel uniqid={uniqid} />
                            )}
                        </CSSEditorTab>
                        <EditorIsBusy uniqid={uniqid} />
                        <EditorIsLocked uniqid={uniqid} />
                        {OVERRIDE_SAVE.includes(editor.name) && <EditorSave uniqid={uniqid} mode={editor.mode} />}
                    </CSSEditorContent>
                </div>
            </CSSEditor>
            {editor?.popup ? (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: '#000', opacity: '.7' }}></div>
            ) : (
                <></>
            )}
        </CSSEditorMask>
    ) : (
        <></>
    );
};
